import React, { useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Mail, MailCheck, ArrowLeft, Loader2 } from 'lucide-react';
import AuthShell, { AuthNotice } from './AuthShell';
import toast from 'react-hot-toast';
import AuthService from '../../services/authService';
import { AuthContext } from '../../contexts/AuthContext';

const MFARecoveryRequest = () => {
  const { user, pendingAuth } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Use user from pendingAuth if available (during MFA flow), otherwise use authenticated user
  const currentUser = pendingAuth?.user || user;
  
  // Debug logging
  console.log('[MFA RECOVERY] user:', user);
  console.log('[MFA RECOVERY] pendingAuth:', pendingAuth);
  console.log('[MFA RECOVERY] currentUser:', currentUser);

  const [email, setEmail] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // If we have authenticated user, use that. Otherwise, require email input.
    if (!currentUser?.email && !email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    
    try {
      if (currentUser?.email) {
        // User is authenticated, use auth-based recovery
        await AuthService.requestMFARecovery();
      } else {
        // User is not authenticated, use email-based recovery
        await AuthService.requestMFARecoveryByEmail(email.trim());
      }
      setSubmitted(true);
      toast.success('Recovery email sent! Check your inbox for instructions.');
    } catch (error) {
      console.error('MFA recovery request failed:', error);
      toast.error('Unable to process recovery request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <AuthShell
        icon={<MailCheck className="h-6 w-6" />}
        title="Recovery Email Sent"
        subtitle={<>We've sent recovery instructions to <strong className="font-medium text-gray-900 dark:text-gray-100">{currentUser?.email || email}</strong></>}
      >
        <p className="text-sm leading-relaxed text-gray-500 dark:text-gray-400">
          Please check your email and follow the instructions to recover access to your account.
          The recovery link will expire in 24 hours.
        </p>
        <Link to="/login" className="btn btn-secondary mt-8 w-full">
          Return to Login
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      icon={<ShieldCheck className="h-6 w-6" />}
      title="MFA Account Recovery"
      subtitle="Can't access your multi-factor authentication device? We'll send you a recovery link to regain access."
      footer={
        <Link to="/login" className="inline-flex items-center gap-1.5 font-medium text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white">
          <ArrowLeft className="h-4 w-4" />
          Back to Login
        </Link>
      }
    >
      <form className="space-y-5" onSubmit={handleSubmit}>
        {/* Account Info Display - show if user is authenticated */}
        {currentUser?.email ? (
          <div className="flex items-center gap-3 rounded-lg border border-primary-200 bg-primary-50 p-4 dark:border-primary-400/30 dark:bg-primary-400/10">
            <Mail className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            <div>
              <p className="text-sm font-medium text-primary-900 dark:text-primary-200">Recovery for account:</p>
              <p className="text-sm text-primary-700 dark:text-primary-300">{currentUser.email}</p>
            </div>
          </div>
        ) : (
          /* Email Input - show if user is not authenticated */
          <div>
            <label htmlFor="email" className="label">
              Email Address
            </label>
            <div className="relative">
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="input h-11 pr-10"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Mail className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            </div>
          </div>
        )}

        <AuthNotice tone="info" role="note">
          <strong className="font-medium">Security Notice:</strong> We'll send you a secure recovery link to help you regain access to your account.
          Recovery links expire in 24 hours and can only be used once.
        </AuthNotice>

        <button type="submit" disabled={loading} className="btn btn-primary btn-lg w-full">
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Sending Recovery Email...
            </>
          ) : (
            'Send Recovery Email'
          )}
        </button>
      </form>
    </AuthShell>
  );
};

export default MFARecoveryRequest;
